const RESET_ONCE_KEY = 'nexxus.cloud_auto_reset';

function deleteIndexedDb(name: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name);
      const timer = window.setTimeout(resolve, 2_500);
      const done = (): void => {
        window.clearTimeout(timer);
        resolve();
      };
      req.onsuccess = done;
      req.onerror = done;
      req.onblocked = done;
    } catch {
      resolve();
    }
  });
}

/** Vide le cache navigateur Cloud (IndexedDB / SW / Cache API). Licence et session restent. */
export async function resetCloudBrowserCaches(): Promise<void> {
  const jobs: Array<Promise<unknown>> = [deleteIndexedDb('NexxusERP')];

  if (typeof indexedDB.databases === 'function') {
    jobs.push(
      indexedDB
        .databases()
        .then((list) =>
          Promise.all(
            list
              .filter((db) => Boolean(db.name && /nexxus/i.test(db.name)))
              .map((db) => deleteIndexedDb(db.name ?? 'NexxusERP')),
          ),
        )
        .catch(() => undefined),
    );
  }

  if (navigator.serviceWorker) {
    jobs.push(
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((reg) => reg.unregister())))
        .catch(() => undefined),
    );
  }

  if (typeof caches !== 'undefined') {
    jobs.push(
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
        .catch(() => undefined),
    );
  }

  await Promise.all(jobs);
}

export function consumeCloudAutoResetFlag(): boolean {
  try {
    if (sessionStorage.getItem(RESET_ONCE_KEY) === '1') {
      sessionStorage.removeItem(RESET_ONCE_KEY);
      return true;
    }
    sessionStorage.setItem(RESET_ONCE_KEY, '1');
    return false;
  } catch {
    return false;
  }
}

export function clearCloudAutoResetFlag(): void {
  try {
    sessionStorage.removeItem(RESET_ONCE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Préférences d'appareil conservées à la fermeture de session (aucune donnée d'entreprise) : acceptations légales de
 * l'appareil, accueil déjà vu, volets, consentements, identifiant d'installation Web, dates de vérification.
 */
const KEPT_LOCAL_KEYS: ReadonlySet<string> = new Set([
  'nexxus_cgu_acceptances_v1',
  'nexxus.welcome.v1',
  'nexxus.sidebar.collapsed',
  'nexxus_auto_update_last_check',
  'nexxus_last_version_check',
  'nexxus_telemetry_consent',
  'nexxus_install_data_consent_v1',
  'nexxus.installation_id',
]);
/** Clés de session d'onglet à conserver : avis « Session expirée » et bascule d'entreprise en cours. */
const KEPT_SESSION_KEYS: ReadonlySet<string> = new Set([
  'nexxus_session_expired_notice',
  'nexxus.web.companies',
  'nexxus.web.company_pending',
]);

function removeKeys(storage: Storage, kept: ReadonlySet<string>, onlyNexxus: boolean): void {
  const doomed: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key === null || kept.has(key)) continue;
    if (!onlyNexxus || /^nexxus/i.test(key)) doomed.push(key);
  }
  for (const key of doomed) storage.removeItem(key);
}

/**
 * Navigateur Cloud, fermeture de session ou changement d'entreprise : efface tout ce qui appartient à l'entreprise
 * ouverte (brouillons de formulaire, fiche entreprise en cache, registres, session mémorisée, base locale
 * IndexedDB, Cache API) pour qu'aucune donnée d'une entreprise ne reste visible ni ne se mélange avec la suivante
 * sur un poste partagé. Les préférences d'appareil listées ci-dessus sont conservées.
 */
export async function purgeCloudBrowserTenantData(): Promise<void> {
  try {
    removeKeys(localStorage, KEPT_LOCAL_KEYS, true);
  } catch {
    /* stockage indisponible (mode privé) */
  }
  try {
    removeKeys(sessionStorage, KEPT_SESSION_KEYS, false);
  } catch {
    /* ignoré */
  }
  await resetCloudBrowserCaches();
}
